// COA (course of action) drawing tools for the planning stage: click-to-add-point sessions that turn
// map gestures into control graphics, and the naming rules that keep a hand-drawn overlay readable
// without ever asking the player to type into a dialog (spec §2 stage 2, §8 — no dropdowns, shapes and
// colours carry the meaning).
//
// Nothing here touches the DOM or the map. The planning stage feeds it points in AO (area of
// operations) centimeters and paints whatever `onPreview` hands back through the Pixi overlay, so the
// same session logic can be driven by a test, by a template plan, or later by an advisor.

import template from '../../data/scenario-template.json';
import type { Graphic, GraphicKind, Scenario } from '../sim/types';

export interface Pt { x: number; y: number }

/** The four graphics a player draws. The assembly area comes from the fitter and is not drawn by hand. */
export type DrawKind = Extract<GraphicKind, 'axis' | 'obj' | 'pl' | 'nai'>;

export interface ToolMeta {
  kind: DrawKind;
  label: string;
  /** Expansion of the abbreviation the graphic is named with, shown once on the tool card. */
  expansion: string;
  /** The shape the tool card is drawn as — the same shape the graphic takes on the map. */
  shape: 'arrow' | 'polygon' | 'line' | 'circle';
  color: string;
  hint: string;
  minPts: number;
  /** A tool that reaches its maximum closes itself: one click is a whole named area of interest. */
  maxPts: number;
}

export const TOOLS: ToolMeta[] = [
  {
    kind: 'axis',
    label: 'Axis of advance',
    expansion: 'AXIS — the route a force attacks along',
    shape: 'arrow',
    color: 'var(--color-friendly)',
    hint: 'Click each turn of the route from the assembly area forward. Double-click or press Enter on the last point.',
    minPts: 2,
    maxPts: Infinity,
  },
  {
    kind: 'obj',
    label: 'Objective',
    expansion: 'OBJ — ground you intend to take and hold',
    shape: 'polygon',
    color: 'var(--color-friendly)',
    hint: 'Click around the ground you intend to seize — three points or more. Double-click or press Enter to close it.',
    minPts: 3,
    maxPts: Infinity,
  },
  {
    kind: 'pl',
    label: 'Phase line',
    expansion: 'PL — a line across the zone you report crossing',
    shape: 'line',
    color: 'var(--color-friendly)',
    hint: 'Click one end, then the other, across the width of the corridor.',
    minPts: 2,
    maxPts: Infinity,
  },
  {
    kind: 'nai',
    label: 'Named area of interest',
    expansion: 'NAI — where you expect to learn something',
    shape: 'circle',
    color: 'var(--color-isr)',
    hint: 'Click the ground you want watched. One click makes the circle.',
    minPts: 1,
    maxPts: 1,
  },
];

export function toolMeta(kind: DrawKind): ToolMeta {
  const t = TOOLS.find((x) => x.kind === kind);
  if (!t) throw new Error(`Unknown drawing tool: ${kind}`);
  return t;
}

/** Default radius of a hand-drawn named area of interest, matching the fitter's own NAIs. */
export const NAI_RADIUS_M = 400;

/**
 * Two clicks closer together than this are the same click. A double-click that finishes a graphic
 * fires two `click` events before the `dblclick`, and on a 15 km map 30 m is about a pixel — so this
 * drops the duplicate without ever refusing a point a player meant to place.
 */
const MIN_SEPARATION_CM = 3000;

// ---- orientation ------------------------------------------------------------------------------------

/**
 * Which way the corridor runs: 'WE' means the attack goes west to east (so the width of the zone is
 * north-south), 'SN' means it goes south to north. Read off a fitted phase line, which the fitter
 * always draws across the full width; falls back to the assembly area -> objective vector.
 */
export function aoOrientation(scenario: Scenario): 'WE' | 'SN' {
  const pl = scenario.graphics.find((g) => g.kind === 'pl' && g.pts.length >= 2);
  if (pl) {
    const dx = Math.abs(pl.pts[1].x - pl.pts[0].x);
    const dy = Math.abs(pl.pts[1].y - pl.pts[0].y);
    return dy >= dx ? 'WE' : 'SN';
  }
  const aa = scenario.graphics.find((g) => g.id === scenario.assemblyId);
  const obj = scenario.graphics.find((g) => g.id === scenario.objectiveId);
  if (aa && obj) {
    const a = centroid(aa.pts);
    const b = centroid(obj.pts);
    return Math.abs(b.x - a.x) >= Math.abs(b.y - a.y) ? 'WE' : 'SN';
  }
  return 'WE';
}

export function centroid(pts: Pt[]): Pt {
  if (pts.length === 0) return { x: 0, y: 0 };
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  return { x: Math.round(x / pts.length), y: Math.round(y / pts.length) };
}

// ---- naming ------------------------------------------------------------------------------------------

/** Phase lines are named for colours, in the order a staff would issue them. */
export const PL_COLORS = ['RED', 'WHITE', 'BLUE', 'GREEN', 'AMBER', 'BLACK'] as const;

const OBJ_NAMES: string[] = (template as { objNames: string[] }).objNames;

export interface NameContext {
  /** Every graphic already on the overlay: the fitted ones plus whatever has been drawn so far. */
  existing: Graphic[];
  orientation: 'WE' | 'SN';
  /** The AO's extent in centimeters, used to place an axis name by its centroid. */
  widthCm: number;
  heightCm: number;
  /** The centroid of the graphic being named (axis only). */
  at?: Pt;
}

function taken(existing: Graphic[]): Set<string> {
  return new Set(existing.map((g) => g.name.toUpperCase()));
}

/**
 * The name a new graphic gets, following the convention already on the overlay: objectives take the
 * next unused name from the scenario's name list (so the fitted objective's own name is skipped),
 * phase lines take the next unused colour after the fitted RED/WHITE/BLUE, named areas of interest
 * count on from the highest number, and an axis is named for where it runs across the corridor.
 */
export function nextGraphicName(kind: DrawKind, ctx: NameContext): string {
  const used = taken(ctx.existing);
  switch (kind) {
    case 'obj': {
      const free = OBJ_NAMES.find((n) => !used.has(`OBJ ${n}`));
      return `OBJ ${free ?? `${ctx.existing.filter((g) => g.kind === 'obj').length + 1}`}`;
    }
    case 'pl': {
      const free = PL_COLORS.find((c) => !used.has(`PL ${c}`));
      return `PL ${free ?? ctx.existing.filter((g) => g.kind === 'pl').length + 1}`;
    }
    case 'nai': {
      let max = 0;
      for (const g of ctx.existing) {
        if (g.kind !== 'nai') continue;
        const m = /(\d+)\s*$/.exec(g.name);
        if (m) max = Math.max(max, Number(m[1]));
      }
      return `NAI ${max + 1}`;
    }
    case 'axis': {
      // Across the width of the corridor: an attack running west to east is named for how far north
      // it goes; one running south to north is named for how far east.
      const names = ctx.orientation === 'WE'
        ? ['SOUTH', 'CENTER', 'NORTH']
        : ['WEST', 'CENTER', 'EAST'];
      const span = Math.max(1, ctx.orientation === 'WE' ? ctx.heightCm : ctx.widthCm);
      const along = ctx.orientation === 'WE' ? ctx.at?.y ?? span / 2 : ctx.at?.x ?? span / 2;
      const frac = Math.min(0.999, Math.max(0, along / span));
      const first = names[Math.floor(frac * 3)];
      const order = [first, ...names.filter((n) => n !== first)];
      const free = order.find((n) => !used.has(`AXIS ${n}`));
      if (free) return `AXIS ${free}`;
      let n = 2;
      while (used.has(`AXIS ${first} ${n}`)) n++;
      return `AXIS ${first} ${n}`;
    }
  }
}

/** A stable, unique id for a hand-drawn graphic; the sim only requires that ids do not collide. */
export function nextGraphicId(kind: DrawKind, existing: Graphic[]): string {
  let n = 1;
  const ids = new Set(existing.map((g) => g.id));
  while (ids.has(`drawn-${kind}-${n}`)) n++;
  return `drawn-${kind}-${n}`;
}

// ---- the drawing session -----------------------------------------------------------------------------

export interface DrawHandlers {
  /** The graphic as it stands, including the point under the pointer; null clears the preview. */
  onPreview: (g: Graphic | null) => void;
  onDone: (g: Graphic) => void;
  onCancel: () => void;
}

export interface DrawSession {
  readonly kind: DrawKind;
  readonly meta: ToolMeta;
  /** Points committed so far, in AO centimeters. */
  points(): Pt[];
  /** Adds a point; a tool at its maximum finishes itself. */
  addPoint(p: Pt): void;
  /** The point under the pointer, drawn into the preview but not committed. */
  hover(p: Pt | null): void;
  /** Double-click or Enter: commits the graphic if it has enough points. Returns what was committed. */
  finish(): Graphic | null;
  /** Escape: throws the in-progress graphic away. */
  cancel(): void;
  /** True once the session has finished or been cancelled; a finished session ignores further input. */
  done(): boolean;
}

/**
 * Starts one drawing session. The caller feeds it map clicks and pointer moves; it hands back a
 * preview graphic to paint (via `Overlay.setPreview`) and, on finish, the real graphic to add to the
 * scenario. Naming happens at finish, so a graphic deleted and redrawn takes the freed name back.
 */
export function startTool(kind: DrawKind, ctx: () => NameContext, handlers: DrawHandlers): DrawSession {
  const meta = toolMeta(kind);
  const pts: Pt[] = [];
  let hoverPt: Pt | null = null;
  let finished = false;

  const previewGraphic = (): Graphic | null => {
    const all = hoverPt && pts.length < meta.maxPts ? [...pts, hoverPt] : pts.slice();
    if (all.length === 0) return null;
    return {
      id: 'draw-preview',
      kind,
      name: '',
      pts: all,
      ...(kind === 'nai' ? { radiusM: NAI_RADIUS_M } : null),
    };
  };

  const emit = (): void => handlers.onPreview(previewGraphic());

  const session: DrawSession = {
    kind,
    meta,
    points: () => pts.slice(),
    done: () => finished,

    addPoint(p) {
      if (finished) return;
      const last = pts[pts.length - 1];
      if (last && Math.hypot(last.x - p.x, last.y - p.y) < MIN_SEPARATION_CM) return;
      pts.push({ x: Math.round(p.x), y: Math.round(p.y) });
      if (pts.length >= meta.maxPts) {
        session.finish();
        return;
      }
      emit();
    },

    hover(p) {
      if (finished) return;
      hoverPt = p;
      emit();
    },

    finish() {
      if (finished) return null;
      if (pts.length < meta.minPts) return null;
      finished = true;
      hoverPt = null;
      handlers.onPreview(null);
      const c = ctx();
      const graphic: Graphic = {
        id: nextGraphicId(kind, c.existing),
        kind,
        name: nextGraphicName(kind, { ...c, at: centroid(pts) }),
        pts: pts.slice(),
        ...(kind === 'nai' ? { radiusM: NAI_RADIUS_M } : null),
      };
      handlers.onDone(graphic);
      return graphic;
    },

    cancel() {
      if (finished) return;
      finished = true;
      hoverPt = null;
      handlers.onPreview(null);
      handlers.onCancel();
    },
  };

  return session;
}
